import React, { useEffect, useRef, useState } from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  createEventSpace,
  getEventSpaceByID,
  EventSpaceImageUpload,
  updateEventSpace,
} from "./services/eventspace.service";
import { clearEventSpaceDetails } from "./eventspaceSlice";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import EventSpaceSpecialtiesArray from "./EventSpaceSpecialtiesArray";
import { useLocation, useNavigate } from "react-router-dom";
import ShadowCard from "../../components/common/ShadowCard";
import { getFile } from "../../_helper/helper";

const CreateEventSpace: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const gridRef = useRef<any>(null);
  const EventSpaceID = location.state?.EventSpaceID;
  const EventSpaceDetail = useAppSelector(
    (state) => state.eventspace.EventSpaceDetail,
  );
  const loading = useAppSelector((state) => state.eventspace.formLoading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [EventSpaceDetail]);

  useEffect(() => {
    if (EventSpaceID) {
      dispatch(getEventSpaceByID(EventSpaceID));
    }
    return () => {
      dispatch(clearEventSpaceDetails());
    };
  }, [EventSpaceID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      EventSpaceTitle: values.EventSpaceTitle ?? "",
      GuestCapacity: values.GuestCapacity ?? "",
      Area: values.Area ? values.Area + "" : "",
      Specialties: values.Specialties ?? "",
      SequenceNo: values.SequenceNo ?? 0,
      IsActive: values.IsActive === 1,
      CreatedBy: 2,
      UpdatedBy: 2,
      EventSpaceSpecialties: values.EventSpaceSpecialties ?? [],
    };

    const ImageData = new FormData();
    const Image =
      values?.Images && typeof values?.Images !== "string"
        ? getFile(values?.Images)
        : "";

    try {
      if (EventSpaceID) {
        const editPayload = {
          ...formData,
          ID: EventSpaceID,
          UpdatedBy: 3,
        };

        const response = await dispatch(updateEventSpace(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (values?.Images && typeof values?.Images !== "string") {
            ImageData.append("Images", Image);
            ImageData.append("ID", EventSpaceID.toString());
            await dispatch(EventSpaceImageUpload(ImageData));
          }
          navigate("/eventspace");
          dispatch(clearEventSpaceDetails());
        }
      } else {
        const response = await dispatch(createEventSpace(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Images &&
            typeof values?.Images !== "string" &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Images", Image);
            ImageData.append("ID", response?.payload?.Data?.ID.toString());
            await dispatch(EventSpaceImageUpload(ImageData));
          }
          navigate("/eventspace");
          dispatch(clearEventSpaceDetails());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleBack = () => {
    navigate("/eventspace");
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={
            EventSpaceDetail || {
              EventSpaceSpecialties: [{ EventSpaceSpecialties: "" }],
              IsActive: 1,
            }
          }
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {EventSpaceID ? "Update Event Space" : "Add Event Space"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="EventSpaceTitle"
                      label="Title"
                      placeholder="Conference Hall A"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="GuestCapacity"
                      label="Guest Capacity"
                      placeholder="50-100"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="Area"
                      label="Area (sq ft)"
                      placeholder="0"
                      format="n0"
                      type="number"
                      component={FormNumericTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperClassName="right-aligned-field"
                      name="SequenceNo"
                      label="Sequence No."
                      placeholder="0"
                      format="n0"
                      component={FormNumericTextField}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => ({
                        value: status?.value,
                        label: status?.label,
                      }))}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={4}>
                    <FieldArray
                      name="EventSpaceSpecialties"
                      component={EventSpaceSpecialtiesArray}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name="Images"
                      label="Image"
                      component={FormImageUpload}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={EventSpaceID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={handleBack}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateEventSpace;
